// Port "Chess" — aus Jans Renderer uebernommen (1.0 RendererWebGL.ts,
// buildChessGroup/animateChess, Zeilen ~1255-1485).
//
// Voll-3D-Schach-Set als object3d-Primitive "chess": 64 Brett-Boxen +
// 32 Figuren aus 6 gebuendelten GLBs (/chess/*.glb, Poly Haven CC0,
// koenig-normalisiert auf 1 Unit). Die Partie (PGN aus primitiveParams)
// wird ueber den progress-Kanal 0..1 nachgespielt; animateChess() posiert
// die fertige Gruppe pro Frame (Figuren-Position/Fade, Zug-Highlights).
//
// PORT-ADAPTIONEN gegenueber 1.0 (alles Verhalten 1:1):
// - Engine (chess-replay.ts, chess.js) und GLTFLoader werden LAZY per
//   dynamic import geladen — buildChessGroup ist ohnehin async, und so
//   landet chess.js nicht im Reader-Bundle von Nicht-Schach-Scrollys.
//   stateAt wird fuer den synchronen Per-Frame-Pfad in userData gestasht.
// - Geometrie-Cache auf Modulebene statt am Renderer-this (eine Seite =
//   ein Cache; Param-/Farb-Rebuilds laden die GLBs nicht neu).
//
// STOLPERFALLEN (aus dem 1.0-Feature, gelten weiter):
// - Jedes Mesh traegt userData.bakedMaterial — updateMaterial darf die
//   Brett-/Figuren-/Label-Materialien NIE ueberschreiben (sonst alles weiss).
// - Layer.setObject ueberspringt chess (sonst ~96 Kind-Layer in der Timeline).
// - Kein Feature darf ueber ALLE Materialien des Sets iterieren und pro
//   Material Uniforms + needsUpdate erzwingen (1.0: Chrome-OOM beim
//   uShadowLift-Masseninjekt). Die heutige Schatten-Staerke laeuft pro
//   LICHT (light.shadow.intensity) und ist davon nicht betroffen.
// - Troika-Labels: MeshBasicMaterial kommt in dieser Pipeline nahezu
//   schwarz raus — emissives MeshStandardMaterial nutzen, Troikas eigenes
//   `color` NICHT setzen.
import THREE from "../utils/THREE";
import { Text } from "troika-three-text";
import { DEFAULT_FONT } from "../Player/assets/fonts";
import { colorToThree } from "../utils/color/threeColor";
import type { Object3DContent } from "@scrolly/post-types";

// GLB-Geometrien ueberleben Rebuilds (Farb-/Param-Aenderung = neuer Build,
// aber kein Re-Fetch der 6 Figuren-Dateien).
const chessGeoCache: Record<string, THREE.BufferGeometry> = {};

export async function buildChessGroup(content: Object3DContent): Promise<THREE.Group> {
    // Engine lazy laden (chess.js); stateAt fuer animateChess mit-stashen.
    const engine = await import("./chess-replay");
    const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");

    const p = (content.primitiveParams || {}) as TODOAny;
    const SQ = Number(p.squareSize) > 0 ? Number(p.squareSize) : 400;
    const group = new THREE.Group();

    const toColor = (c: TODOAny, fb: number[]) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        colorToThree((c || { r: fb[0], g: fb[1], b: fb[2], a: 1 }) as any);
    const light = toColor(p.boardLight, [235, 235, 210]);
    const dark = toColor(p.boardDark, [120, 150, 100]);
    const highlight = toColor(p.boardHighlight, [247, 201, 69]);
    const whiteCol = toColor(p.whitePieceColor, [240, 240, 235]);
    const blackCol = toColor(p.blackPieceColor, [40, 40, 45]);

    /*
     * BRETT ALS EIN MESH, nicht 64 Boxen (Jans Fund 05.08.2026: „An den
     * Rändern jedes einzelnen Quadrates flackert es leicht bei Bewegung —
     * vielleicht ganz dünne Lücken?" — genau das). 64 einzeln gezeichnete
     * Kästchen stoßen nur MATHEMATISCH bündig: Jede Kante wird pro Kästchen
     * für sich gerundet und geglättet, und die letzte Nachkommastelle
     * entscheidet, ob zwischen zwei Kacheln eine haarfeine Fuge aufblitzt.
     *
     * Deshalb: EINE Deckplatte als zusammenhängende Geometrie. Die Feldfarben
     * kommen als VERTEXFARBEN mit — benachbarte Felder benutzen dieselben
     * Kantenwerte aus derselben Formel (bit-identisch), da KANN keine Fuge
     * entstehen. Zug-Highlights schreiben die sechs Eckfarben des Feldes um
     * (paintSquare/animateChess), kein Material-Wechsel nötig. Die
     * Brettdicke liefert ein einzelner Sockel-Kasten darunter.
     */
    const boardH = SQ * 0.25;
    // reflection 0..1 (matt -> glaenzend) steuert roughness/metalness
    const refl = Math.max(0, Math.min(1, Number(p.reflection ?? 0.15)));

    const plattenPos = new Float32Array(64 * 6 * 3);
    const plattenNrm = new Float32Array(64 * 6 * 3);
    const plattenCol = new Float32Array(64 * 6 * 3);
    /** Erster Vertex-Index jedes Feldes (6 Vertices pro Feld). */
    const squareStart: Record<string, number> = {};
    /** Grundfarbe jedes Feldes, fuer das Zuruecksetzen nach einem Highlight. */
    const baseColors: Record<string, THREE.Color> = {};
    let vi = 0;
    for (let f = 0; f < 8; f += 1) {
        for (let r = 0; r < 8; r += 1) {
            const sq = String.fromCharCode(97 + f) + (r + 1);
            const base = (f + r) % 2 === 1 ? light : dark; // a1 dunkel
            // a-Linie muss von Weiss aus LINKS (= +X) liegen -> File-Achse
            // spiegeln (a1 = Weiss' nahes linkes Eckfeld); nur POSITIONEN
            // spiegeln, nie Geometrie. Kanten aus EINER Formel je Linie:
            // rechter Rand von Feld f = linker Rand von Feld f+1, dieselbe
            // Rechnung, derselbe Float — wasserdicht.
            const x0 = (3 - f) * SQ;
            const x1 = (4 - f) * SQ;
            const z0 = (r - 4) * SQ;
            const z1 = (r - 3) * SQ;
            squareStart[sq] = vi;
            baseColors[sq] = base.clone();
            // zwei Dreiecke, y=0, Normale +Y. LAUFRICHTUNG zaehlt: von oben
            // gesehen GEGEN den Uhrzeigersinn, sonst gilt die Flaeche als
            // Rueckseite und wird von oben weggelassen (Jans Fund: Brett
            // unsichtbar, Figuren schwebten — nur die Seitenwaende standen).
            const ecken = [
                [x0, z0],
                [x1, z1],
                [x1, z0],
                [x0, z0],
                [x0, z1],
                [x1, z1],
            ];
            for (const [x, z] of ecken) {
                plattenPos[vi * 3] = x;
                plattenPos[vi * 3 + 1] = 0;
                plattenPos[vi * 3 + 2] = z;
                plattenNrm[vi * 3 + 1] = 1;
                plattenCol[vi * 3] = base.r;
                plattenCol[vi * 3 + 1] = base.g;
                plattenCol[vi * 3 + 2] = base.b;
                vi += 1;
            }
        }
    }
    /*
     * SEITEN UND BODEN IN DIESELBE GEOMETRIE — kein separater Sockel-Kasten.
     * Die erste Fassung legte einen Kasten ein Haar unter die Platte; in
     * Jans echtem Schach-Scrolly (Kamera weit weg, geschrumpfte Welt) reichte
     * die Tiefen-Praezision dafuer nicht und Platte und Kasten-Deckel
     * flimmerten grossflaechig gegeneinander (Z-Fighting — die grosse
     * Diagonale im Stoermuster war die Dreieckskante des Kasten-Deckels).
     * Ein GESCHLOSSENER Koerper aus einem Stueck hat schlicht keine zweite
     * Flaeche, die kaempfen koennte.
     */
    const seiten: number[][] = [];
    const rand = 4 * SQ;
    const unten = -boardH;
    // vier Seitenwaende (Normale nach aussen), dann der Boden (Normale -Y)
    const wand = (
        ax: number, az: number, bx: number, bz: number, nx: number, nz: number,
    ) => {
        // zwei Dreiecke a-oben, b-oben, b-unten / a-oben, b-unten, a-unten
        seiten.push(
            [ax, 0, az, nx, 0, nz], [bx, 0, bz, nx, 0, nz], [bx, unten, bz, nx, 0, nz],
            [ax, 0, az, nx, 0, nz], [bx, unten, bz, nx, 0, nz], [ax, unten, az, nx, 0, nz],
        );
    };
    wand(-rand, -rand, rand, -rand, 0, -1); // vorne  (-Z)
    wand(rand, rand, -rand, rand, 0, 1); // hinten (+Z)
    wand(rand, -rand, rand, rand, 1, 0); // rechts (+X)
    wand(-rand, rand, -rand, -rand, -1, 0); // links  (-X)
    seiten.push(
        [-rand, unten, -rand, 0, -1, 0], [rand, unten, -rand, 0, -1, 0], [rand, unten, rand, 0, -1, 0],
        [-rand, unten, -rand, 0, -1, 0], [rand, unten, rand, 0, -1, 0], [-rand, unten, rand, 0, -1, 0],
    );

    const gesamt = 64 * 6 + seiten.length;
    const pos = new Float32Array(gesamt * 3);
    const nrm = new Float32Array(gesamt * 3);
    const col = new Float32Array(gesamt * 3);
    pos.set(plattenPos);
    nrm.set(plattenNrm);
    col.set(plattenCol);
    for (let i = 0; i < seiten.length; i += 1) {
        const [x, y, z, nx, ny, nz] = seiten[i];
        const o = (64 * 6 + i) * 3;
        pos[o] = x;
        pos[o + 1] = y;
        pos[o + 2] = z;
        nrm[o] = nx;
        nrm[o + 1] = ny;
        nrm[o + 2] = nz;
        col[o] = dark.r;
        col[o + 1] = dark.g;
        col[o + 2] = dark.b;
    }

    const plattenGeo = new THREE.BufferGeometry();
    plattenGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    plattenGeo.setAttribute("normal", new THREE.BufferAttribute(nrm, 3));
    const colorAttr = new THREE.BufferAttribute(col, 3);
    plattenGeo.setAttribute("color", colorAttr);
    const plattenMat = new THREE.MeshStandardMaterial({
        vertexColors: true,
        // Brett spiegelt milder als die Figuren, damit das Muster lesbar bleibt
        roughness: 0.85 - 0.7 * refl,
        metalness: 0.08 * refl,
    });
    // explizit opak: bleibt im opaken Pass und damit auch im
    // Transmission-Target sichtbar (Lupe ueber dem Brett)
    plattenMat.userData = { alphaNeeded: false };
    const platte = new THREE.Mesh(plattenGeo, plattenMat);
    platte.receiveShadow = true;
    platte.castShadow = true;
    platte.userData.bakedMaterial = true;
    group.add(platte);

    /**
     * Die sechs Eckfarben eines Feldes umschreiben (Highlight/Reset).
     * Schreibt in den LEBENDEN Farbpuffer `col` der Gesamt-Geometrie — die
     * Felder liegen dort an denselben Indizes 0..383 wie beim Aufbau
     * (`plattenCol` war nur die Bauhilfe und wurde hineinkopiert).
     */
    const paintSquare = (sq: string, c: THREE.Color) => {
        const start = squareStart[sq];
        if (start === undefined) return;
        for (let i = 0; i < 6; i += 1) {
            col[(start + i) * 3] = c.r;
            col[(start + i) * 3 + 1] = c.g;
            col[(start + i) * 3 + 2] = c.b;
        }
    };

    // Rand-Koordinaten a-h + 1-8, FLACH auf allen vier Kanten (wie gedruckt):
    // nahe Kante je Spieler aufrecht lesbar, Gegenkante 180 Grad gedreht.
    const coordLabels: TODOAny[] = [];
    if (p.showCoordinates !== false) {
        const coordHex = toColor(p.coordColor, [60, 60, 60]).getHex();
        const edge = 4 * SQ;
        const off = SQ * 0.42;
        const blackQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
        const whiteQuat = new THREE.Quaternion()
            .setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI)
            .multiply(blackQuat);
        const makeLabel = (str: string, x: number, z: number, white: boolean) => {
            const label = new Text() as TODOAny;
            label.text = str;
            label.font = DEFAULT_FONT;
            label.fontSize = SQ * 0.5;
            label.anchorX = "center";
            label.anchorY = "middle";
            // MeshBasic rendert hier nahezu schwarz (ungelitete Flachfarben
            // werden gecrusht) -> emissives Standard-Material, Farbe leuchtet
            // lichtunabhaengig; Troikas eigenes `color` bewusst NICHT setzen.
            label.material = new THREE.MeshStandardMaterial({
                color: 0x000000,
                emissive: coordHex,
                emissiveIntensity: 1,
                roughness: 1,
                metalness: 0,
                transparent: true,
                side: THREE.DoubleSide,
            });
            label.material.userData = { alphaNeeded: true };
            label.quaternion.copy(white ? whiteQuat : blackQuat);
            label.position.set(x, SQ * 0.02, z);
            label.castShadow = false;
            label.receiveShadow = false;
            label.userData.bakedMaterial = true;
            label.sync();
            group.add(label);
            coordLabels.push(label);
        };
        const files = "abcdefgh";
        for (let f = 0; f < 8; f += 1) {
            const x = (3.5 - f) * SQ; // File-Achse gespiegelt (a auf +X)
            makeLabel(files[f], x, -edge - off, true); // Weiss' Kante (vorn)
            makeLabel(files[f], x, edge + off, false); // Schwarz' Kante (hinten)
        }
        for (let r = 0; r < 8; r += 1) {
            const z = (r - 3.5) * SQ;
            makeLabel(String(r + 1), edge + off, z, true); // Weiss' Kante (links)
            makeLabel(String(r + 1), -edge - off, z, false); // Schwarz' Kante (rechts)
        }
    }

    // 6 Figuren-Geometrien (gecacht -> Farb-/Param-Tweaks ohne Re-Fetch)
    const loader = new GLTFLoader();
    const loadGeo = (url: string) =>
        new Promise<THREE.BufferGeometry>((res, rej) => {
            if (chessGeoCache[url]) return res(chessGeoCache[url]);
            loader.load(
                url,
                (g: TODOAny) => {
                    let geo: THREE.BufferGeometry | null = null;
                    g.scene.traverse((o: TODOAny) => {
                        if (!geo && o.isMesh) geo = o.geometry;
                    });
                    if (!geo) return rej(new Error("no mesh in " + url));
                    chessGeoCache[url] = geo;
                    res(geo);
                },
                undefined,
                rej
            );
        });
    const FILE: Record<string, string> = {
        k: "king",
        q: "queen",
        r: "rook",
        b: "bishop",
        n: "knight",
        p: "pawn",
    };
    // PARALLEL laden, nicht nacheinander: die alte for/await-Schleife lud
    // Koenig -> Dame -> ... -> Bauer streng seriell — auf dev gemessen begann
    // der Bauer erst bei ~1,4 s nach START (Jans Fund „~2 Sekunden bis das
    // Spiel laeuft" am Handy; alle 6 Dateien zusammen nur ~274 KB). Parallel
    // kostet die Runde so lange wie die langsamste Einzeldatei.
    const geos: Record<string, THREE.BufferGeometry> = {};
    const typen = Object.keys(FILE);
    const geladen = await Promise.all(
        typen.map((t) => loadGeo("/chess/" + FILE[t] + ".glb"))
    );
    typen.forEach((t, i) => {
        geos[t] = geladen[i];
    });

    /*
     * Figuren sind koenig-normalisiert (1 Unit); Koenig ~1.6 REFERENZ-Felder
     * hoch. BEWUSST NICHT an SQ gekoppelt (bis 05.08.2026: SQ*1.6): Damit
     * skalierte FELDGROESSE das ganze Set — exakt das, was der
     * Skalieren-Regler des Layers schon kann (Jans Fund: „das kann ich ja
     * unten mit dem Slider"). Jetzt aendert FELDGROESSE das Brett-Raster
     * UNTER den Figuren: kleinere Felder = gedraengteres Brett, groessere =
     * mehr Luft — die Figuren behalten ihre Groesse. Beim Vorgabewert 400
     * sieht alles aus wie vorher.
     */
    const REFERENZ_SQ = 400;
    const pieceScale = REFERENZ_SQ * 1.6;

    let game: TODOAny;
    try {
        game =
            p.pgn && String(p.pgn).trim() ? engine.buildGame(String(p.pgn)) : engine.initialGame();
    } catch (e) {
        game = engine.initialGame(); // ungueltige PGN -> Grundstellung
    }

    const pieceMeshes: Record<string, THREE.Mesh> = {};
    for (const id of Object.keys(game.pieces)) {
        const info = game.pieces[id];
        const mat = new THREE.MeshStandardMaterial({
            color: (info.color === "w" ? whiteCol : blackCol).clone(),
            // Reflexe kommen aus dem Szenen-HDRI: weniger roughness = schaerfer
            roughness: 0.85 - 0.8 * refl,
            metalness: 0.15 * refl,
            transparent: true, // Schlag-Fade ohne Recompiles
        });
        mat.userData = { alphaNeeded: true };
        const mesh = new THREE.Mesh(geos[info.type], mat);
        mesh.scale.setScalar(pieceScale);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.pieceId = id;
        mesh.userData.curType = info.type;
        mesh.userData.color = info.color;
        mesh.userData.bakedMaterial = true;
        // Springer sind asymmetrisch: schwarze zum Gegner drehen
        if (info.type === "n") mesh.rotation.y = info.color === "b" ? Math.PI : 0;
        group.add(mesh);
        pieceMeshes[id] = mesh;
    }

    group.userData.__chess = {
        SQ,
        game,
        pieceMeshes,
        geos,
        pieceScale,
        highlight,
        coordLabels,
        // Brett-Geometrie: Feldfarben leben als Vertexfarben in `col`
        plattenGeo,
        plattenCol: col,
        paintSquare,
        baseColors,
        // fuer den synchronen Per-Frame-Pfad (Engine ist lazy geladen)
        stateAt: engine.stateAt,
    };
    animateChess(group, 0); // Grundstellung als Startpose
    return group;
}

// Gruppe auf progress 0..1 posieren (pro Frame aus renderLayer).
export function animateChess(group: THREE.Group, progress: number) {
    const cx = (group.userData as TODOAny).__chess;
    if (!cx) return;
    const { SQ, game, pieceMeshes, geos, highlight } = cx;
    const st = cx.stateAt(game, progress);
    for (const id in pieceMeshes) pieceMeshes[id].visible = false;
    for (const rp of st.pieces) {
        const mesh = pieceMeshes[rp.id] as THREE.Mesh | undefined;
        if (!mesh) continue;
        mesh.visible = true;
        // File-Achse gespiegelt wie beim Brett (a-Linie auf Weiss' links/+X)
        mesh.position.set(-rp.x * SQ, rp.y * SQ, rp.z * SQ);
        if (mesh.userData.curType !== rp.type && geos[rp.type]) {
            mesh.geometry = geos[rp.type];
            mesh.userData.curType = rp.type;
            // Springer nach Bauern-Umwandlung weiter zum Gegner drehen
            mesh.rotation.y = rp.type === "n" ? (mesh.userData.color === "b" ? Math.PI : 0) : 0;
        }
        const mat = mesh.material as THREE.MeshStandardMaterial;
        if (mat.opacity !== rp.opacity) mat.opacity = rp.opacity;
    }
    // Feldfarben: alles auf Grundfarbe, dann die Highlights — direkt in die
    // Vertexfarben der Deckplatte (1152 Floats, pro Frame vernachlaessigbar).
    for (const sq in cx.baseColors) cx.paintSquare(sq, cx.baseColors[sq]);
    for (const hs of st.highlights) cx.paintSquare(hs, highlight);
    (cx.plattenGeo.getAttribute("color") as THREE.BufferAttribute).needsUpdate = true;
}
